import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    ReactFlow,
    ReactFlowProvider,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTranslation } from "react-i18next";

const rid = () => Math.random().toString(36).slice(2, 9);
const isJ = (id) => typeof id === "string" && id.startsWith("J_");

// 🔹 Custom Node
const CustomNode = ({ id, data, selected }) => {
    const isJunction = id.startsWith("J_");
    const hasConnection = data?.hasConnection ?? true;
    const selectedBorder = selected ? "3px solid #f59e0b" : undefined;

    const [isEditing, setIsEditing] = useState(data.editing || false);
    const [label, setLabel] = useState(data?.label || "");
    const inputRef = useRef(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select(); // highlight edilmiş gelsin
        }
    }, [isEditing]);

    const handleDoubleClick = () => {
        // 🔹 Sadece sanal cihazlar (V_) düzenlenebilir
        if (!isJunction && id.startsWith("V_")) {
            setIsEditing(true);
        }
    };


    const handleBlur = () => {
        setIsEditing(false);
        if (data.onRename) data.onRename(id, label);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            setIsEditing(false);
            if (data.onRename) data.onRename(id, label);
        }
    };

    if (isJunction)
        return (
            <div
                className="relative flex items-center justify-center cursor-grab active:cursor-grabbing transition-transform hover:scale-110"
                data-drag-handle
                style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#1f2937",
                    border: selectedBorder || "3px solid #d1d5db",
                    boxShadow: "0 0 6px rgba(0,0,0,0.6)",
                }}
                title={data?.label || "Junction"}
            >
                {[
                    { pos: "Top", color: "#10b981" },
                    { pos: "Left", color: "#10b981" },
                    { pos: "Right", color: "#3b82f6" },
                    { pos: "Bottom", color: "#3b82f6" },
                ].map(({ pos, color }) => (
                    <Handle
                        key={pos}
                        id={pos.toLowerCase()}
                        type={["Top", "Left"].includes(pos) ? "target" : "source"}
                        position={Position[pos]}
                        isConnectable
                        style={{
                            width: 14,
                            height: 14,
                            background: color,
                            border: "2px solid white",
                            borderRadius: "50%",
                        }}
                    />
                ))}
            </div>
        );

    return (
        <div
            className="rounded-lg px-4 py-2 text-sm shadow-md select-none transition-transform hover:scale-[1.02]"
            onDoubleClick={handleDoubleClick}
            data-drag-handle
            style={{
                background: hasConnection ? "#fff" : "#fee2e2",
                border:
                    selectedBorder ||
                    (hasConnection ? "2px solid #a3a3a3" : "2px solid #ef4444"),
                boxShadow: hasConnection
                    ? "0 0 6px rgba(0,0,0,0.2)"
                    : "0 0 8px rgba(239,68,68,0.6)",
                cursor: isEditing ? "text" : "grab",
                width: 120,
            }}
        >
            {[{ pos: "Top", type: "target" },
            { pos: "Left", type: "target" },
            { pos: "Right", type: "source" },
            { pos: "Bottom", type: "source" }]
                .map(({ pos, type }) => (
                    <Handle
                        key={pos}
                        id={pos.toLowerCase()}
                        type={type}
                        position={Position[pos]}
                        isConnectable
                    />
                ))}
            {isEditing ? (
                <input
                    ref={inputRef}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            setIsEditing(false);
                            if (data.onRename) data.onRename(id, label);
                        }
                        // 🧠 Backspace ve Delete node'u silmesin:
                        e.stopPropagation();
                    }}
                    className="w-full text-center border-none outline-none text-sm bg-transparent"
                />

            ) : (
                <div className="text-center text-gray-800">{label}</div>
            )}
        </div>
    );
};

// 🔸 Edge renkleri
const edgeStyle = (label) =>
    label === "parallel"
        ? { stroke: "#10b981", strokeWidth: 2.5 }
        : { stroke: "#3b82f6", strokeWidth: 2.5 };

// ⚙️ Topoloji hesaplama
function processCircuitTopology(nodes, edges) {
    const nodeIds = new Set(nodes.map((n) => n.id));
    const inDeg = {};
    const outDeg = {};

    const cleaned = edges.filter(
        (e) => nodeIds.has(e.source) && nodeIds.has(e.target)
    );

    cleaned.forEach((e) => {
        outDeg[e.source] = (outDeg[e.source] || 0) + 1;
        inDeg[e.target] = (inDeg[e.target] || 0) + 1;
    });

    const updatedNodes = nodes.map((n) => {
        const hasConnection = cleaned.some(
            (e) => e.source === n.id || e.target === n.id
        );
        return { ...n, data: { ...n.data, hasConnection } };
    });

    const updatedEdges = cleaned.map((e) => {
        let label = "series";
        if (isJ(e.source) && outDeg[e.source] > 1) label = "parallel";
        if (isJ(e.target) && inDeg[e.target] > 1) label = "parallel";
        return { ...e, label, style: edgeStyle(label), type: "smoothstep" };
    });

    return { nodes: updatedNodes, edges: updatedEdges };
}

function HierarchyPageInner() {
    const { t } = useTranslation();
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token");

    // --- State & Refs ---
    const [devices, setDevices] = useState([]);
    const [rf, setRf] = useState(null);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveWarning, setSaveWarning] = useState("");
    const [hasHierarchy, setHasHierarchy] = useState(false);
    const [mode, setMode] = useState("view"); // "view" | "edit"
    const [showResetModal, setShowResetModal] = useState(false);


    const history = useRef([]);
    const future = useRef([]);
    const dragging = useRef(false);

    // --- Helpers ---
    const pushHistory = useCallback((nodes, edges) => {
        history.current.push({
            nodes: JSON.parse(JSON.stringify(nodes)),
            edges: JSON.parse(JSON.stringify(edges)),
        });
        if (history.current.length > 50) history.current.shift();
        future.current = [];
    }, []);

    const undo = useCallback(() => {
        if (history.current.length < 2) return;
        const current = history.current.pop();
        future.current.push(current);
        const prev = history.current[history.current.length - 1];
        setNodes(prev.nodes);
        setEdges(prev.edges);
    }, [setNodes, setEdges]);

    const redo = useCallback(() => {
        if (!future.current.length) return;
        const next = future.current.pop();
        history.current.push(next);
        setNodes(next.nodes);
        setEdges(next.edges);
    }, [setNodes, setEdges]);

    const findUnconnectedDevices = useCallback(() => {
        return nodes.filter(
            (n) =>
                !isJ(n.id) &&
                !edges.some((e) => e.source === n.id || e.target === n.id)
        );
    }, [nodes, edges]);

    const onConnect = useCallback(
        (params) => {
            if (params.source === params.target) {
                alert(t('hierarchy.selfConnectError'));
                return;
            }
            const newEdge = {
                id: `${params.source}-${params.target}-${rid()}`,
                source: params.source,
                target: params.target,
                sourceHandle: params.sourceHandle,
                targetHandle: params.targetHandle,
            };
            setEdges((prev) => {
                const updated = [...prev, newEdge];
                const processed = processCircuitTopology(nodes, updated);
                setNodes(processed.nodes);
                pushHistory(processed.nodes, processed.edges);
                return processed.edges;
            });
        },
        [nodes, pushHistory, setEdges, setNodes]
    );

    const addNodeAtCenter = (node) => {
        if (!rf) return;
        const viewport = rf.getViewport();
        const position = {
            x: -viewport.x / viewport.zoom + window.innerWidth / 2 - 75,
            y: -viewport.y / viewport.zoom + window.innerHeight / 2 - 25,
        };
        setNodes((prev) => {
            const newNodes = [...prev, { ...node, position }];
            pushHistory(newNodes, edges);
            return newNodes;
        });
    };

    const handleAddDevice = (device) => {
        const id = device.id.toString();
        if (nodes.some((n) => n.id === id)) return;
        addNodeAtCenter({ id, type: "custom", data: { label: device.name } });
        setShowDeviceModal(false);
    };
    const handleRenameVirtual = (id, newLabel) => {
        // 🔹 React state'i güncelle (gerçek kaynak state)
        setNodes((nds) =>
            nds.map((n) =>
                n.id === id ? { ...n, data: { ...n.data, label: newLabel } } : n
            )
        );
    };




    const handleAddJunction = () => {
        addNodeAtCenter({
            id: `J_${rid()}`,
            type: "custom",
            data: { label: "Junction" },
        });
    };

    const handleSaveConfirm = async () => {
        if (!rf) return; // ReactFlow instance yoksa dur

        // 🔥 En güncel nodeları ve bağlantıları React Flow'dan al
        const currentNodes = [...nodes];
        const currentEdges = [...edges];

        // --- Sanal cihazların (V_ ile başlayan) kaynaklarını otomatik doldur ---
        const updatedNodes = currentNodes.map((node) => {
            if (node.id.startsWith("V_")) {
                // Bu sanal cihaza gelen bağlantıları bul
                const incomingEdges = currentEdges.filter((e) => e.target === node.id);
                // Gelen bağlantılardaki source id’lerini al
                const sources = incomingEdges.map((e) => e.source);

                return {
                    ...node,
                    data: {
                        ...node.data,
                        formula: {
                            ...node.data.formula,
                            sources,
                        },
                    },
                };
            }
            return node;
        });

        // 🔍 Bağlantısı olmayan cihazları filtrele
        const unconnected = updatedNodes.filter(
            (n) =>
                !isJ(n.id) &&
                !currentEdges.some((e) => e.source === n.id || e.target === n.id)
        );

        const filteredNodes = updatedNodes.filter((n) => !unconnected.includes(n));

        // 🔹 Güncel payload
        const payload = { nodes: filteredNodes, edges: currentEdges };

        //console.log("📦 Gönderilen nodelar:", JSON.stringify(filteredNodes, null, 2));

        if (filteredNodes.length === 0) {
            alert(t('hierarchy.noConnectedDevices'));
            setShowSaveModal(false);
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/hierarchy/save`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const json = await res.json();

            if (json.success) {
                alert(t('hierarchy.saveSuccess'));
            } else {
                // Backend özel hata mesajı gönderiyor mu?
                if (json.error && json.error.includes("has no children")) {
                    alert(t('hierarchy.virtualDeviceError'));
                } else {
                    alert(`${t('hierarchy.saveError')}\n\n` + (json.error || ""));
                }
            }

        } catch (err) {
            alert(`${t('hierarchy.serverError')}: ` + err.message);
        }

        setShowSaveModal(false);
    };




    const handleResetHierarchy = async () => {
        try {
            const res = await fetch(`${API_BASE}/hierarchy/reset`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (json.success) {
                alert(t('hierarchy.resetSuccess'));
                setNodes([]);
                setEdges([]);
                setHasHierarchy(false);
                setMode("view");
            } else {
                alert(t('hierarchy.resetFail'));
            }
        } catch (err) {
            alert(`${t('hierarchy.serverError')}: ` + err.message);
        }
        setShowResetModal(false);
    };


    const onNodeDragStart = useCallback(() => {
        dragging.current = true;
    }, []);

    const onNodeDragStop = useCallback(
        (_, node) => {
            if (dragging.current) {
                pushHistory(nodes, edges);
                dragging.current = false;
            }
        },
        [nodes, edges, pushHistory]
    );

    // --- Effects ---
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const res = await fetch(`${API_BASE}/hierarchy/load`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json();
                if (json.success && json.nodes?.length > 0) {
                    setNodes(json.nodes);
                    setEdges(json.edges);
                    setHasHierarchy(true);
                    // 🧠 İlk state’i history’ye kaydet:
                    history.current = [{ nodes: json.nodes, edges: json.edges }];
                } else {
                    setHasHierarchy(false);
                }

            } catch (err) {
                console.error("loadHierarchy error:", err);
                setHasHierarchy(false);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [API_BASE, token, setNodes, setEdges]);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/gateways/devices/customer`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                setDevices(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("device load error", err);
            }
        })();
    }, [API_BASE, token]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key.toLowerCase() === "s") {
                e.preventDefault();
                const unconnected = findUnconnectedDevices();
                if (unconnected.length > 0) {
                    setSaveWarning(
                        `⚠️ ${unconnected.length} ${t('hierarchy.unconnectedWarning')}: ${unconnected
                            .map((u) => u.data?.label || u.id)
                            .join(", ")}`
                    );
                } else setSaveWarning("");
                setShowSaveModal(true);
            }

            if (e.key === "Delete" || e.key === "Backspace") {
                setNodes((nds) => {
                    const filtered = nds.filter((n) => !n.selected);
                    setEdges((eds) => {
                        const filteredEdges = eds.filter((e) => !e.selected);
                        pushHistory(filtered, filteredEdges);
                        return filteredEdges;
                    });
                    return filtered;
                });
            }
            if (e.ctrlKey && e.key.toLowerCase() === "z") {
                e.preventDefault();
                undo();
            }
            if (e.ctrlKey && e.key.toLowerCase() === "y") {
                e.preventDefault();
                redo();
            }
            if (e.ctrlKey && e.key.toLowerCase() === "f") {
                e.preventDefault();
                if (!document.fullscreenElement)
                    document.documentElement.requestFullscreen();
                else document.exitFullscreen();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [undo, redo, pushHistory, findUnconnectedDevices]);



    // --- Render ---
    return (
        <div className="w-full h-screen flex bg-white overflow-hidden">
            {/* Loading state */}
            {isLoading ? (
                <div className="flex items-center justify-center h-screen text-gray-500 w-full">
                    {t('hierarchy.loading')}
                </div>
            ) : !hasHierarchy && mode === "view" ? (
                // Empty hierarchy
                <div className="flex flex-col items-center justify-center h-screen text-center bg-gray-50 w-full">
                    <h2 className="text-xl font-semibold text-gray-700 mb-3">
                        {t('hierarchy.empty')}
                    </h2>
                    <button
                        onClick={() => setMode("edit")}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-whit    e rounded"
                    >
                        {t('hierarchy.editTopology')}
                    </button>
                </div>
            ) : hasHierarchy && mode === "view" ? (
                // View mode
                <div className="w-full h-screen flex flex-col items-center justify-center bg-gray-50">
                    <h2 className="text-xl font-semibold text-gray-700 mb-3">
                        {t('hierarchy.currentTopology')}
                    </h2>
                    <div className="w-[80%] h-[70vh] border shadow bg-white rounded-lg overflow-hidden">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={{ custom: CustomNode }}
                            fitView
                            nodesDraggable={false}
                            nodesConnectable={false}
                        >
                            <MiniMap />
                            <Controls />
                            <Background />
                        </ReactFlow>
                    </div>
                    <button
                        onClick={() => setMode("edit")}
                        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                        {t('hierarchy.editTopology')}
                    </button>
                </div>
            ) : (
                // Edit mode
                <>
                    <div className="flex-1 relative">
                        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between bg-gray-50 border-b px-4 py-2">
                            <h1 className="text-lg font-semibold text-gray-700">
                                {t('hierarchy.editorTitle')}
                            </h1>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowResetModal(true)}
                                    className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                                >
                                    {t('hierarchy.reset')}
                                </button>

                                <button
                                    onClick={undo}
                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                >
                                    {t('hierarchy.back')}
                                </button>
                                <button
                                    onClick={redo}
                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                >
                                    {t('hierarchy.forward')}
                                </button>
                                <button
                                    onClick={() => {
                                        const unconnected =
                                            findUnconnectedDevices();
                                        if (unconnected.length > 0) {
                                            setSaveWarning(
                                                `⚠️ ${unconnected.length
                                                } ${t('hierarchy.unconnectedWarning')}: ${unconnected
                                                    .map(
                                                        (u) =>
                                                            u.data?.label ||
                                                            u.id
                                                    )
                                                    .join(", ")}`
                                            );
                                        } else setSaveWarning("");
                                        setShowSaveModal(true);
                                    }}
                                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                                >
                                    {t('hierarchy.save')}
                                </button>
                                <button
                                    onClick={() =>
                                        !document.fullscreenElement
                                            ? document.documentElement.requestFullscreen()
                                            : document.exitFullscreen()
                                    }
                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                >
                                    {t('hierarchy.fullScreen')}
                                </button>
                            </div>
                        </div>

                        <div className="w-full h-full mt-[48px]">
                            <ReactFlow
                                nodes={nodes.map((n) =>
                                    n.id.startsWith("V_")
                                        ? { ...n, data: { ...n.data, onRename: handleRenameVirtual } }
                                        : n
                                )}
                                edges={edges}
                                nodeTypes={{ custom: CustomNode }}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                onInit={setRf}
                                onNodeDragStart={onNodeDragStart}
                                onNodeDragStop={onNodeDragStop}
                                nodesDraggable
                                nodesConnectable
                                fitView
                            >

                                <MiniMap />
                                <Controls />
                                <Background />
                            </ReactFlow>
                        </div>
                    </div>

                    {/* Sağ panel */}
                    <div className="w-60 border-l bg-gray-100 flex flex-col p-4 gap-3">
                        <h2 className="font-semibold text-gray-700 text-base mb-1 border-b pb-1">
                            {t('hierarchy.toolPanel')}
                        </h2>
                        <button
                            onClick={() => setShowDeviceModal(true)}
                            className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                            {t('hierarchy.addDevice')}
                        </button>
                        <button
                            onClick={handleAddJunction}
                            className="px-3 py-2 bg-gray-700 text-white rounded text-sm hover:bg-gray-800"
                        >
                            {t('hierarchy.addJunction')}
                        </button>
                        <button
                            onClick={() => {
                                const count = nodes.filter((n) => n.id.startsWith("V_")).length + 1;
                                const id = `V_${rid()}`;
                                addNodeAtCenter({
                                    id,
                                    type: "custom",
                                    data: {
                                        label: `${t('hierarchy.virtualDevicePrefix')} #${count}`,
                                        formula: { sources: [], field: "", operation: "sum" },
                                        editing: true,
                                    },

                                });
                            }}
                            className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        >
                            {t('hierarchy.addVirtualDevice')}
                        </button>

                    </div>

                    {/* Modals */}
                    {showDeviceModal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
                            <div className="bg-white rounded-lg shadow-lg p-5 w-96">
                                <h2 className="text-lg font-semibold mb-3 text-gray-800">
                                    {t('hierarchy.addFromExisting')}
                                </h2>
                                {isLoading ? (
                                    <p className="text-gray-500 text-sm p-3">
                                        {t('hierarchy.loading')}
                                    </p>
                                ) : (
                                    <div className="max-h-60 overflow-y-auto border rounded">
                                        {devices.map((d) => {
                                            const isAdded = nodes.some(
                                                (n) => n.id === d.id.toString()
                                            );
                                            return (
                                                <button
                                                    key={d.id}
                                                    disabled={isAdded}
                                                    onClick={() =>
                                                        handleAddDevice(d)
                                                    }
                                                    className={`w-full text-left px-3 py-2 border-b text-sm ${isAdded
                                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                        : "hover:bg-gray-100"
                                                        }`}
                                                >
                                                    {d.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="flex justify-end mt-3">
                                    <button
                                        onClick={() =>
                                            setShowDeviceModal(false)
                                        }
                                        className="px-4 py-1 bg-gray-300 hover:bg-gray-400 rounded text-sm"
                                    >
                                        {t('hierarchy.close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showSaveModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001]">
                            <div className="bg-white rounded-lg shadow-lg p-6 w-[420px]">
                                <h2 className="text-lg font-semibold mb-2 text-gray-800">
                                    {t('hierarchy.confirmSave')}
                                </h2>
                                {saveWarning ? (
                                    <p className="text-red-600 text-sm mb-4">
                                        {saveWarning}
                                    </p>
                                ) : (
                                    <p className="text-gray-600 text-sm mb-4">
                                        {t('hierarchy.confirmSaveMsg')}
                                    </p>
                                )}
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowSaveModal(false)}
                                        className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                    >
                                        {t('hierarchy.cancel')}
                                    </button>
                                    {saveWarning ? (
                                        <button
                                            onClick={handleSaveConfirm}
                                            className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm"
                                        >
                                            {t('hierarchy.saveAnyway')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleSaveConfirm}
                                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                                        >
                                            {t('hierarchy.save')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {showResetModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001]">
                            <div className="bg-white rounded-lg shadow-lg p-6 w-[420px]">
                                <h2 className="text-lg font-semibold mb-2 text-gray-800">{t('hierarchy.resetTitle')}</h2>
                                <p className="text-gray-600 text-sm mb-4">
                                    {t('hierarchy.resetMsg')}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowResetModal(false)}
                                        className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                                    >
                                        {t('hierarchy.cancel')}
                                    </button>
                                    <button
                                        onClick={handleResetHierarchy}
                                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                                    >
                                        {t('hierarchy.resetConfirm')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </>
            )}
        </div>
    );
}

export default function HierarchyPage() {
    return (
        <ReactFlowProvider>
            <HierarchyPageInner />
        </ReactFlowProvider>
    );
}
